/**
 * 🔊 미니게임 효과음 유틸 (Web Audio API 합성음)
 * - 외부 오디오 파일 없이 Oscillator 로 합성
 * - 브라우저 정책상 최초 사용자 제스처 이후에만 재생 가능 (자동 unlock)
 * - localStorage 'sfx_muted' 로 전역 음소거 토글
 */

let ctx: AudioContext | null = null;
let unlocked = false;

function getCtx(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (isMuted()) return null;
    if (!ctx) {
        try {
            const AC = (window.AudioContext || (window as any).webkitAudioContext);
            if (!AC) return null;
            ctx = new AC();
        } catch {
            return null;
        }
    }
    if (ctx.state === 'suspended') {
        try { ctx.resume(); } catch { /* ignore */ }
    }
    return ctx;
}

export function isMuted(): boolean {
    try { return localStorage.getItem('sfx_muted') === '1'; } catch { return false; }
}

export function setMuted(m: boolean) {
    try {
        if (m) localStorage.setItem('sfx_muted', '1');
        else localStorage.removeItem('sfx_muted');
    } catch { /* ignore */ }
}

/**
 * 브라우저 정책 대응 — 최초 user gesture 에서 audio context unlock.
 * (사용자가 버튼 눌렀을 때 호출되면 됨)
 */
export function unlockSfx() {
    if (unlocked) return;
    unlocked = true;
    const c = getCtx();
    if (c) {
        // 무음 톤 하나 재생해서 내부 상태 활성화
        const o = c.createOscillator();
        const g = c.createGain();
        g.gain.value = 0;
        o.connect(g).connect(c.destination);
        o.start();
        o.stop(c.currentTime + 0.01);
    }
}

function tone(opts: {
    freq: number;
    duration: number;      // s
    type?: OscillatorType;
    attack?: number;       // s
    release?: number;      // s
    volume?: number;       // 0~1
    freqEnd?: number;      // 주파수 슬라이드 종료값
    delay?: number;        // 시작 지연 s
}) {
    const c = getCtx();
    if (!c) return;
    const now = c.currentTime + (opts.delay || 0);
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = opts.type || 'sine';
    o.frequency.setValueAtTime(opts.freq, now);
    if (opts.freqEnd != null) {
        o.frequency.linearRampToValueAtTime(opts.freqEnd, now + opts.duration);
    }
    const vol = opts.volume != null ? opts.volume : 0.2;
    const attack = opts.attack != null ? opts.attack : 0.005;
    const release = opts.release != null ? opts.release : 0.08;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(vol, now + attack);
    g.gain.linearRampToValueAtTime(vol, now + opts.duration - release);
    g.gain.linearRampToValueAtTime(0, now + opts.duration);
    o.connect(g).connect(c.destination);
    o.start(now);
    o.stop(now + opts.duration + 0.02);
}

/** 짧은 클릭 — 사다리 한 칸, 릴 1 틱 */
export function playTick() {
    tone({ freq: 900, duration: 0.05, type: 'square', volume: 0.12, release: 0.03 });
}

/** 팡파레 — 3음 상승 (C-E-G) */
export function playWin() {
    tone({ freq: 523.25, duration: 0.12, type: 'triangle', volume: 0.22 });
    tone({ freq: 659.25, duration: 0.12, type: 'triangle', volume: 0.22, delay: 0.1 });
    tone({ freq: 783.99, duration: 0.2, type: 'triangle', volume: 0.25, delay: 0.2 });
}

/** 깜짝 팡파레 — 5음 상승 (레이스 피니시) */
export function playRaceFanfare() {
    const notes = [392, 523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => tone({ freq: f, duration: 0.14, type: 'square', volume: 0.2, delay: i * 0.1 }));
    tone({ freq: 1046.5, duration: 0.3, type: 'triangle', volume: 0.25, delay: 0.6 });
}

/** 병 도는 소리 — 3.5초, 피치 하강 (sawtooth 저주파) */
export function playWhoosh(durationSec = 3.5) {
    tone({
        freq: 420, freqEnd: 90, duration: durationSec,
        type: 'sawtooth', volume: 0.1, attack: 0.1, release: 0.2,
    });
    // 상위 하모닉 약하게 추가
    tone({
        freq: 200, freqEnd: 60, duration: durationSec,
        type: 'triangle', volume: 0.06, attack: 0.1, release: 0.2,
    });
}

/** 정지 — 퍽 */
export function playPop() {
    tone({ freq: 1200, freqEnd: 400, duration: 0.18, type: 'square', volume: 0.25, release: 0.1 });
}

/** 룰렛 스핀 중 반복 틱 — 매 ~100ms */
export function playSlotTick() {
    tone({ freq: 2000, duration: 0.03, type: 'square', volume: 0.1 });
}

/** 일반 버튼 피드백 */
export function playClick() {
    tone({ freq: 660, duration: 0.04, type: 'sine', volume: 0.12 });
}
